import { CircleHelp, Star } from 'lucide-react';
import type { CSSProperties } from 'react';
import { DisclosureTooltip } from '../DisclosureTooltip/DisclosureTooltip';

export interface NumericLayoutProps {
  starRating?: number;
  starRatingTooltip?: string;
  reviewLink?: string;
  hideLabel?: boolean;
  iconSize?: number | { xs: number; md: number };
  tooltipColor?: string;
  tooltipIconColor?: string;
}

function getIconSize(iconSize: NumericLayoutProps['iconSize']) {
  return typeof iconSize === 'number' ? iconSize : (iconSize?.xs ?? 24);
}

export function NumericLayout({
  starRating,
  starRatingTooltip,
  reviewLink,
  hideLabel = false,
  iconSize = 24,
  tooltipColor = 'var(--foreground)',
  tooltipIconColor = 'var(--foreground)',
}: NumericLayoutProps) {
  const starSize = getIconSize(iconSize);
  const responsiveIconClass =
    typeof iconSize === 'number'
      ? ''
      : 'h-[var(--icon-size-xs)] w-[var(--icon-size-xs)] md:h-[var(--icon-size-md)] md:w-[var(--icon-size-md)]';
  const responsiveIconStyle =
    typeof iconSize === 'number'
      ? undefined
      : ({
          '--icon-size-xs': `${iconSize.xs}px`,
          '--icon-size-md': `${iconSize.md}px`,
        } as CSSProperties);

  if (!starRating) {
    return (
      <span className="typography-text-small block text-muted-foreground">
        <Star
          aria-hidden
          className={`inline align-bottom text-border ${responsiveIconClass}`}
          data-testid="numeric-rating-star"
          fill="currentColor"
          height={starSize}
          style={responsiveIconStyle}
          width={starSize}
        />{' '}
        Not yet rated
      </span>
    );
  }

  return (
    <div className="flex flex-col">
      {!hideLabel || starRatingTooltip ? (
        <div className="inline-flex items-center">
          {!hideLabel ? (
            <span className="typography-text-small pr-0.5 text-muted-foreground">
              NerdWallet rating
            </span>
          ) : null}
          {starRatingTooltip ? (
            <DisclosureTooltip
              trigger={
                <button
                  aria-label="NerdWallet rating info"
                  className="typography-text-small inline-flex cursor-pointer bg-transparent p-0 align-middle"
                  style={{ color: tooltipIconColor } as CSSProperties}
                  type="button"
                >
                  <CircleHelp aria-hidden size="0.813rem" />
                </button>
              }
            >
              <span className="typography-text-small" style={{ color: tooltipColor }}>
                {starRatingTooltip}
              </span>
            </DisclosureTooltip>
          ) : null}
        </div>
      ) : null}
      <div className="mr-0.5 flex items-center text-2xl">
        <Star
          aria-hidden
          className={`mr-[5px] fill-primary text-primary ${responsiveIconClass}`}
          height={starSize}
          style={responsiveIconStyle}
          width={starSize}
        />
        {reviewLink ? (
          <a href={reviewLink} rel="noopener" target="_blank">
            {starRating.toFixed(1)}
          </a>
        ) : (
          starRating.toFixed(1)
        )}
        <span className="typography-text-small pt-1 pl-0.5 text-muted-foreground">/5</span>
      </div>
    </div>
  );
}
